// MA-57: front-door fork on deviceMode + role.
// kiosk → /play (kid session; preserves existing behavior, the default)
// personal + parent → /parent (parent dashboard on the parent's own phone)
// personal + teacher → /teacher (teacher area; content built out by MA-61)
//
// The App.jsx gate ladder still runs above the Outlet for every route,
// so a logged-out / un-onboarded user still sees Login → Onboarding → …
// before this redirect resolves — same guarantee as before MA-03.
//
// Existing users: pw_user_role defaults to "parent" when unset, so all
// current personal-mode users (pw_device_mode = "personal", no pw_user_role)
// still land on /parent — no regression.
//
// MA-289 slice (b): a `role` search param (stamped into the magic-link
// emailRedirectTo by signInWithEmailOtp) restamps pw_user_role on landing —
// covers the cross-tab/cross-device case where the tap-time localStorage
// write from LoginScreen never reached this browser context. See
// roleOverrideFromSearch's doc comment in userRole.ts for why.
//
// MA-550 (founder live-QA 2026-08-22): reads getDeviceModeRaw() here, NOT
// getDeviceMode() — the permissive getDeviceMode() default ("kiosk" for a
// never-set device) is exactly what silently discarded a first-time
// teacher's role choice pre-fix (resolveFrontDoorDestination's header in
// userRole.ts has the full root-cause + the 3×2 matrix this now resolves).
//
// MA-1056: while a website "Set up a free class" entry is pending (captured
// from /teacher/start by main.jsx, src/lib/teacher/pendingTeacherStart.ts),
// the front door sends the visitor to the teacher area's Class setup tab. This
// is how a Google sign-in, which returns to the app root, gets back there.
// With nothing pending the fork below is unchanged.
import { createFileRoute, redirect } from "@tanstack/react-router";
import { getDeviceModeRaw } from "../lib/deviceMode";
// MA-928 — an armed class door device boots to the door (/play/who).
import { getArmedClass } from "../lib/teacher/kioskArming";
import {
  getUserRole,
  resolveFrontDoorDestination,
  roleOverrideFromSearch,
  setUserRole,
} from "../lib/userRole";
import { clearPendingTeacherStart, readPendingTeacherStart, teacherStartDestination } from "../lib/teacher/pendingTeacherStart";

export const Route = createFileRoute("/")({
  // Carry the incoming search params through the redirect. The app's
  // dev-auth bypass (?dev=1, read once on mount in useAuth) and any
  // deep-link params (utm/ref/…) would otherwise be dropped at the
  // front door, signing a fresh visitor out before the target resolves.
  beforeLoad: ({ search }) => {
    const roleOverride = roleOverrideFromSearch((search as Record<string, unknown>)?.role);
    if (roleOverride) setUserRole(roleOverride);
    // An explicit parent role in the URL (the magic-link return) wins over a
    // pending entry (MA-1056 RED r1 SHOULD-3).
    if (roleOverride === "parent") clearPendingTeacherStart();
    if (readPendingTeacherStart()) throw redirect(teacherStartDestination(search as Record<string, unknown>));
    const to = resolveFrontDoorDestination(getDeviceModeRaw(), getUserRole(), getArmedClass() !== null);
    throw redirect({ to, search });
  },
  component: () => null,
});
